import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import whiteLogo from "@/assets/logos/internal/WaytoraWhiteTextLogo.svg";
import { formatImageUrl } from "@/lib/general";
import { FeatureSection } from "./aboutComponents/FeatureSection";
import { IntegrationsSection } from "./aboutComponents/Integrations";
import { CtaSection } from "./aboutComponents/CTASection";
import { AboutStyles } from "./aboutComponents/AboutStyles";
import TripCardsCarousel from "./aboutComponents/carasouelTrips";
import Header from "@/components/Header";
import {
  GlobeAltIcon,
  SparklesIcon,
  TagIcon,
  UserGroupIcon,
  CommandLineIcon,
  DevicePhoneMobileIcon,
  BuildingOfficeIcon,
  MagnifyingGlassIcon,
  HomeIcon,
  ClockIcon,
  FaceSmileIcon,
  LightBulbIcon,
} from "@heroicons/react/24/outline";

const BackgroundPhoto = ({
  top,
  left,
  width,
  height,
  delay,
  src,
}: {
  top: number;
  left: number;
  width: string;
  height: string;
  delay: number;
  src: string;
}) => (
  <div
    className="absolute rounded-xl overflow-hidden"
    style={{
      top: `${top}%`,
      left: `${left}%`,
      width: width,
      height: height,
      animation: `float 20s ease-in-out ${delay}s infinite`,
    }}
  >
    <img
      src={formatImageUrl(src, "high", true)}
      alt=""
      loading="eager"
      className="w-full h-full object-cover opacity-80 blur-xs"
      style={{ transform: "scale(1.1)" }}
    />
  </div>
);

const AboutPage = () => {
  const navigate = useNavigate();

  const photos = [
    {
      width: "450px",
      height: "350px",
      top: 60,
      left: 5,
      delay: 0,
      src: "https://public.waytora.com/Waytora%20About%20IMGs/IMG1.jpg",
    },
    {
      width: "400px",
      height: "300px",
      top: 60,
      left: 75,
      delay: 1,
      src: "https://public.waytora.com/Waytora%20About%20IMGs/IMG2.jpg",
    },
    {
      width: "420px",
      height: "320px",
      top: 15,
      left: 35,
      delay: 2,
      src: "https://public.waytora.com/Waytora%20About%20IMGs/IMG3.jpg",
    },
    {
      width: "430px",
      height: "330px",
      top: 65,
      left: 40,
      delay: 2.5,
      src: "https://public.waytora.com/Waytora%20About%20IMGs/IMG5.jpg",
    },
    {
      width: "410px",
      height: "310px",
      top: 35,
      left: 35,
      delay: 0.5,
      src: "https://public.waytora.com/Waytora%20About%20IMGs/IMG6.jpg",
    },
    {
      width: "440px",
      height: "340px",
      top: 15,
      left: 0,
      delay: 1.8,
      src: "https://public.waytora.com/Waytora%20About%20IMGs/IMG7.jpg",
    },
    {
      width: "390px",
      height: "320px",
      top: 10,
      left: 75,
      delay: 2.2,
      src: "https://public.waytora.com/Waytora%20About%20IMGs/IMG8.jpg",
    },
  ];

  useEffect(() => {
    const abortController = new AbortController();
    const imageObjects: HTMLImageElement[] = [];

    const preloadImages = async () => {
      try {
        const imageLoadPromises = photos.map((photo) => {
          return new Promise((resolve, reject) => {
            const img = new Image();
            imageObjects.push(img);

            img.onload = () => resolve(img);
            img.onerror = () =>
              reject(new Error(`Failed to load image: ${photo.src}`));
            img.src = formatImageUrl(photo.src, "high", true);
          });
        });

        await Promise.all(imageLoadPromises);
      } catch (error) {
        console.error("Error preloading images:", error);
      }
    };

    if (!abortController.signal.aborted) {
      preloadImages();
    }

    return () => {
      abortController.abort();
      imageObjects.forEach((img) => {
        img.onload = null;
        img.onerror = null;
        img.src = "";
      });
    };
  }, []);

  return (
    <div className="flex flex-col min-h-screen">
      {/* Top section with floating background */}
      <div className="relative w-full min-h-screen flex flex-col bg-black text-white overflow-hidden">
        <style>
          {`
            @keyframes float {
              0%, 100% { transform: translate(0, 0) rotate(0deg); }
              25% { transform: translate(15px, -15px) rotate(1deg); }
              50% { transform: translate(30px, 0) rotate(-1deg); }
              75% { transform: translate(15px, 15px) rotate(0.5deg); }
            }

            /* Hide some background photos on small screens */
            @media (max-width: 768px) {
              .bg-photo-mobile-hidden {
                display: none;
              }
              .bg-photo-mobile-visible {
                width: 80% !important;
                height: auto !important;
              }
            }

            /* Custom Styles for New Sections */
            .section-title {
              /* Optional: Add subtle text shadow or gradient */
              /* text-shadow: 0 1px 3px rgba(0, 0, 0, 0.5); */
            }

            .image-placeholder-wrapper {
              border: 1px solid rgba(255, 255, 255, 0.1);
              transition: transform 0.3s ease, box-shadow 0.3s ease;
            }
            .image-placeholder-wrapper:hover {
              transform: scale(1.03);
              box-shadow: 0 10px 25px rgba(0, 0, 0, 0.4);
            }
            .image-placeholder {
              background: linear-gradient(145deg, #2d3748, #1a202c); /* Dark gradient */
              border-radius: inherit; /* Inherit wrapper's rounding */
              text-align: center;
              padding: 1rem;
            }
            .image-placeholder p {
              opacity: 0.7;
            }

            .section-offer {
              background: linear-gradient(to bottom, #111827, #000000); /* Subtle gradient from dark gray to black */
            }

            .offer-card {
              background-color: rgba(17, 24, 39, 0.6); /* Slightly darker than bg-black/50 */
              border-color: rgba(55, 65, 81, 0.7); /* gray-700 with alpha */
              transition: transform 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease;
              position: relative;
              overflow: hidden;
            }
            .offer-card:hover {
              transform: translateY(-5px);
              border-color: rgba(99, 102, 241, 0.6); /* indigo-500/60 */
              box-shadow: 0 8px 20px rgba(99, 102, 241, 0.15);
            }

            .offer-card-title {
              /* Optional: Add specific styling */
            }
            .offer-card-text {
              color: #9ca3af; /* gray-400 */
              font-size: 0.95rem;
              line-height: 1.6;
            }

            .section-standout {
              /* background: linear-gradient(to bottom, #000000, #111827); */ /* Optional gradient */
            }

            .benefit-item {
              background-color: rgba(17, 24, 39, 0.3); /* Subtle background */
              padding: 1rem 1rem 1rem 1.5rem; /* Adjust padding */
              border-radius: 0 8px 8px 0; /* Rounded corners on the right */
              border-left-width: 5px; /* Slightly thicker border */
              transition: background-color 0.3s ease, border-color 0.3s ease;
              position: relative; /* For potential pseudo-elements */
            }
            .benefit-item:hover {
              background-color: rgba(30, 41, 59, 0.5); /* Slightly lighter on hover */
              border-color: #818cf8; /* Lighter indigo on hover */
            }
            .benefit-title {
              /* Optional: Add styling */
            }
            .benefit-text {
              color: #9ca3af; /* gray-400 */
              font-size: 0.95rem;
              line-height: 1.6;
            }

            /* Responsive styles */
            @media (max-width: 640px) {
              .section-title {
                font-size: 2.5rem !important;
              }
              .benefit-item {
                padding: 0.75rem 0.75rem 0.75rem 1rem;
              }
              .offer-card-text, .benefit-text {
                font-size: 0.9rem;
              }
            }
          `}
        </style>

        {/* Background Photos Layer */}
        <div className="absolute inset-0 overflow-hidden">
          {photos.map((photo, index) => (
            <BackgroundPhoto
              key={index}
              width={photo.width}
              height={photo.height}
              top={photo.top}
              left={photo.left}
              delay={photo.delay}
              src={photo.src}
            />
          ))}
        </div>

        {/* Content Layer */}
        <Header showHomeButton={true} />

        <main className="flex-grow relative flex items-center justify-center px-4 -translate-y-6 sm:-translate-y-10">
          <div className="max-w-xl w-full px-4 sm:px-8 py-8 sm:py-12 text-center relative z-20 backdrop-blur-xl bg-black/30 rounded-2xl border border-white/20 shadow-2xl">
            <div className="text-center mb-6 sm:mb-8">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-normal mb-4 sm:mb-6 section-title bg-clip-text text-transparent bg-gradient-to-r from-indigo-200 to-white">
                Our Story
              </h2>
              <div className="w-16 sm:w-24 h-1 bg-indigo-500 mx-auto rounded-full"></div>
            </div>
            <p className="text-sm sm:text-md font-light text-gray-300 mb-4 sm:mb-8">
              As avid travellers ourselves, we found the organising and booking
              of travel for groups to be super clunky and slow.
            </p>
            <p className="text-sm sm:text-md font-light text-gray-300 mb-4 sm:mb-8">
              To respond to mess, we made{" "}
              <span className="text-indigo-200 font-medium">Waytora</span>, a
              collaborative planning process where you can find everything you
              need in one place.
            </p>
            <p className="text-sm sm:text-md font-light text-gray-300 mb-4 sm:mb-8">
              Click to begin or scroll down to learn more.
            </p>
            <div className="flex flex-col sm:flex-row justify-center space-y-3 sm:space-y-0 sm:space-x-4">
              <button
                onClick={() => navigate("/signup")}
                className="group relative bg-transparent px-4 sm:px-6 py-2 text-sm sm:text-base md:text-lg font-medium text-white 
                overflow-hidden rounded-lg border border-white/30 backdrop-blur-sm
                hover:border-white/50 hover:scale-105 transition-all duration-300 ease-out"
              >
                <span className="relative z-10">Start Your Journey</span>
                <div
                  className="absolute inset-0 h-full w-full translate-y-full bg-white/10
                  group-hover:translate-y-0 transition-transform duration-300 ease-in-out"
                ></div>
              </button>
              <button
                onClick={() => navigate("/contact")}
                className="group relative bg-transparent px-4 sm:px-6 py-2 text-sm sm:text-base md:text-lg font-medium text-white 
                overflow-hidden rounded-lg border border-white/30 backdrop-blur-sm
                hover:border-white/50 hover:scale-105 transition-all duration-300 ease-out"
              >
                <span className="relative z-10">Contact Us</span>
                <div
                  className="absolute inset-0 h-full w-full translate-y-full bg-white/10
                  group-hover:translate-y-0 transition-transform duration-300 ease-in-out"
                ></div>
              </button>
            </div>
          </div>
        </main>
      </div>

      {/* Our Mission Section */}
      <div className="bg-black text-white py-16 sm:py-24 px-4 section-mission relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-indigo-900/20 to-transparent pointer-events-none"></div>

        <div className="max-w-6xl mx-auto relative">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-normal mb-4 sm:mb-6 section-title bg-clip-text text-transparent bg-gradient-to-r from-indigo-200 to-white">
              Our Mission
            </h2>
            <div className="w-16 sm:w-24 h-1 bg-indigo-500 mx-auto rounded-full"></div>
          </div>

          <div className="max-w-3xl mx-auto px-4 sm:px-8 py-8 sm:py-12 text-center relative z-20 backdrop-blur-xl bg-black/30 rounded-2xl border border-white/20 shadow-2xl">
            <div className="space-y-6 sm:space-y-8">
              <p className="text-sm sm:text-md font-light text-gray-300 mb-4 sm:mb-8">
                Waytora's mission is to revolutionize the way people plan and
                experience travel. We believe that travel should be exciting and
                stress-free, from the initial spark of wanderlust to the moment
                you step off the plane.
              </p>
              <p className="text-sm sm:text-md font-light text-gray-300 mb-4 sm:mb-8">
                We're committed to providing our users with the tools and
                community they need to create unforgettable journeys.
              </p>
              <div className="flex justify-center items-center space-x-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-indigo-500/20 flex items-center justify-center">
                  <SparklesIcon className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-300" />
                </div>
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-indigo-500/20 flex items-center justify-center">
                  <GlobeAltIcon className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-300" />
                </div>
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-indigo-500/20 flex items-center justify-center">
                  <TagIcon className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-300" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* What We Offer Section */}
      <div className="bg-gradient-to-b from-gray-900 to-black text-white py-16 sm:py-24 px-4 section-offer relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(99,102,241,0.1),transparent_70%)] pointer-events-none"></div>

        <div className="max-w-6xl mx-auto relative">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-normal mb-4 sm:mb-6 section-title bg-clip-text text-transparent bg-gradient-to-r from-indigo-200 to-white">
              What We Offer
            </h2>
            <div className="w-16 sm:w-24 h-1 bg-indigo-500 mx-auto rounded-full"></div>
          </div>

          <p className="text-base sm:text-xl text-center text-gray-300 mb-10 sm:mb-16 offer-subtitle max-w-3xl mx-auto backdrop-blur-xl bg-black/30 rounded-2xl border border-white/20 shadow-2xl p-4 sm:p-6">
            Waytora is your all-in-one travel companion, offering a
            comprehensive suite of features designed to streamline your trip
            planning:
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-8 offer-grid">
            {[
              {
                title: "Effortless Collaboration",
                text: "Waytora enables users to create, edit, and book trips together in one place. No more scattered information or endless coordination headaches!",
                icon: UserGroupIcon,
              },
              {
                title: "AI-Powered Planning",
                text: "Our platform uses AI to assist with itinerary automation and provide intelligent support throughout the planning process. Let AI take the strain out of decision-making and discover hidden gems.",
                icon: CommandLineIcon,
              },
              {
                title: "Centralized Trip Management",
                text: "Keep all your essential travel details organized in one convenient location. Access your itinerary, bookings, and travel documents anytime, anywhere.",
                icon: DevicePhoneMobileIcon,
              },
              {
                title: "The Trip Builder",
                text: "Design customisable trips with friends, using AI to suggest destinations, activities, and more. Tailor your adventure to your unique preferences and interests.",
                icon: BuildingOfficeIcon,
              },
              {
                title: "The Explore Page",
                text: "Discover trip templates and find inspiration from a world of travellers. Share your own journeys and spark ideas for your next getaway.",
                icon: MagnifyingGlassIcon,
              },
              {
                title: "The Lodge",
                text: "Connect with fellow adventurers, share your travel stories, and build excitement for upcoming trips in our vibrant social network.",
                icon: HomeIcon,
              },
            ].map((offer, index) => (
              <div key={index} className="group relative">
                <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/20 to-transparent rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                <div className="bg-black/50 backdrop-blur-sm p-4 sm:p-8 rounded-2xl border border-gray-700/50 hover:border-indigo-500/50 transition-all duration-500 relative h-full">
                  <div className="text-2xl sm:text-4xl mb-4 sm:mb-6">
                    <offer.icon className="w-6 h-6 sm:w-8 sm:h-8 text-indigo-300" />
                  </div>
                  <h3 className="text-xl sm:text-2xl font-medium mb-2 sm:mb-4 text-indigo-300 offer-card-title">
                    {offer.title}
                  </h3>
                  <p className="text-gray-400 text-sm sm:text-base offer-card-text leading-relaxed">
                    {offer.text}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Why Waytora Stands Out Section */}
      <div className="bg-black text-white py-16 sm:py-24 px-4 section-standout relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(99,102,241,0.05),transparent_70%)] pointer-events-none"></div>

        <div className="max-w-6xl mx-auto relative">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-normal mb-4 sm:mb-6 section-title bg-clip-text text-transparent bg-gradient-to-r from-indigo-200 to-white">
              Why Waytora Stands Out
            </h2>
            <div className="w-16 sm:w-24 h-1 bg-indigo-500 mx-auto rounded-full"></div>
          </div>

          <div className="space-y-4 sm:space-y-6">
            <div className="backdrop-blur-xl bg-black/30 rounded-2xl border border-white/20 shadow-2xl p-4 sm:p-8">
              {[
                {
                  title: "Saves You Time",
                  text: "Waytora streamlines the planning process, allowing you to focus on the joy of travel.",
                  icon: ClockIcon,
                },
                {
                  title: "Reduces Stress",
                  text: "Our intuitive platform and AI assistance take away the common frustrations of organising group trips.",
                  icon: FaceSmileIcon,
                },
                {
                  title: "Enhances Collaboration",
                  text: "Waytora makes planning with friends and family a breeze, ensuring everyone is on the same page.",
                  icon: UserGroupIcon,
                },
                {
                  title: "Provides Inspiration",
                  text: "Discover new destinations and experiences through our community and AI-driven suggestions.",
                  icon: LightBulbIcon,
                },
              ].map((benefit, index) => (
                <div key={index} className="group mb-4 sm:mb-6 last:mb-0">
                  <div className="border-l-4 border-indigo-500 pl-3 sm:pl-6 benefit-item hover:bg-indigo-500/10 transition-all duration-300 rounded-r-xl py-2 sm:py-3">
                    <div className="flex items-center space-x-2 sm:space-x-4 mb-1 sm:mb-2">
                      <benefit.icon className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-300" />
                      <h3 className="text-lg sm:text-xl font-medium text-indigo-300 benefit-title">
                        {benefit.title}
                      </h3>
                    </div>
                    <p className="text-gray-400 text-sm sm:text-base benefit-text ml-6 sm:ml-8">
                      {benefit.text}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Additional content from the first HTML file */}
      {/* <div className="bg-black">
        <FeatureSection />
        <TripCardsCarousel />

        <IntegrationsSection />
        <CtaSection />
      </div> */}
    </div>
  );
};

export default AboutPage;
